import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  Sparkles, 
  Zap, 
  Heart, 
  Droplets, 
  Activity, 
  Shield,
  MessageCircle,
  Phone,
  CheckCircle,
  ArrowRight,
  Clock,
  Award
} from 'lucide-react';

const Services = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-hero text-foreground">Our Premium Services</h1>
            <p className="text-xl text-primary font-medium">Comprehensive wellness solutions tailored to your needs</p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Discover our full range of advanced beauty, health, and wellness treatments designed to help you 
              achieve your goals and enhance your natural radiance.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Book Consultation
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full sm:w-auto">
                  <Phone className="h-4 w-4 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Body Contouring */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
              <div className="relative">
                <img 
                  src="./images/body_contouring_treatment_2.jpeg" 
                  alt="Advanced body contouring treatment"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground">Body Contouring</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Transform your body with our advanced non-invasive contouring treatments using cutting-edge technology 
                    for natural, lasting results.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Treatment Options:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Cavitation Therapy</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">RF Technology</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Laser Treatments</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Vacuum Bipolar RF</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Wood Therapy</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Benefits:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Reduces stubborn fat deposits</li>
                    <li>• Improves skin texture and firmness</li>
                    <li>• Non-invasive with no downtime</li>
                    <li>• Visible results after first session</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">60-90 minutes per session</span>
                  </div>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* IV Drip Therapy */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
              <div className="relative">
                <img 
                  src="./images/iv_drip_therapy_1.jpeg" 
                  alt="IV drip therapy wellness treatment"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                    <Droplets className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground">IV Drip Therapy</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Boost your wellness from within with our customized IV drip treatments designed to enhance energy, 
                    support weight loss, and promote radiant skin.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Available Drips:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Weight Loss Drip</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Energy Boost Drip</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Glow Enhancement</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Anti-Aging Drip</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Immunity Boost</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Custom Blends</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Benefits:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• 100% nutrient absorption</li>
                    <li>• Immediate energy boost</li>
                    <li>• Enhanced skin radiance</li>
                    <li>• Supports weight management</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">30-45 minutes per session</span>
                  </div>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Therapeutic Massage */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
              <div className="relative">
                <img 
                  src="./images/wellness_massage_therapy_1.jpeg" 
                  alt="Professional therapeutic massage treatment"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground">Therapeutic Massage</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Relax, detox, and rejuvenate with our professional massage treatments designed to promote 
                    total body wellness and stress relief.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Massage Types:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Relaxation Massage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Deep Tissue</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Detox Massage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Lymphatic Drainage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Hot Stone Therapy</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Benefits:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Reduces stress and tension</li>
                    <li>• Improves circulation</li>
                    <li>• Promotes better sleep</li>
                    <li>• Enhances overall well-being</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">60-90 minutes per session</span>
                  </div>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Slimming & Glow Injections */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center shadow-elegant">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Slimming & Glow Injections</h3>
                    <p className="text-primary font-medium">Targeted enhancement treatments</p>
                  </div>
                </div>
                
                <p className="text-muted-foreground leading-relaxed">
                  Specialized injection treatments designed to target stubborn fat deposits and enhance your natural 
                  skin radiance for a more confident you.
                </p>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Treatment Options:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Fat Dissolving Injections</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Skin Brightening</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Vitamin Injections</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Glow Enhancement</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Benefits:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Targets specific problem areas</li>
                    <li>• Enhances skin radiance</li>
                    <li>• Quick and effective treatments</li>
                    <li>• Minimal recovery time</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">15-30 minutes per session</span>
                  </div>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Medical Weight Loss */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center shadow-elegant">
                    <Activity className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Medical Weight Loss</h3>
                    <p className="text-primary font-medium">Comprehensive wellness programs</p>
                  </div>
                </div>
                
                <p className="text-muted-foreground leading-relaxed">
                  Personalized weight management programs that combine medical expertise with lifestyle guidance 
                  for sustainable, healthy weight loss results.
                </p>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Program Includes:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Health Assessment</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Personalized Plan</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Nutritional Guidance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Progress Monitoring</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Lifestyle Coaching</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Ongoing Support</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Benefits:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Medically supervised approach</li>
                    <li>• Sustainable weight loss</li>
                    <li>• Improved overall health</li>
                    <li>• Long-term lifestyle changes</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Ongoing program</span>
                  </div>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Butt Vacuum */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center shadow-elegant">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Butt Vacuum</h3>
                    <p className="text-primary font-medium">Body enhancement therapy</p>
                  </div>
                </div>
                
                <p className="text-muted-foreground leading-relaxed">
                  Specialized vacuum therapy treatment designed to enhance and lift for natural-looking body 
                  contouring results using advanced suction technology.
                </p>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Treatment Features:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Non-invasive procedure</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Natural enhancement</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Immediate results</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Safe and effective</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Benefits:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Enhances natural curves</li>
                    <li>• Improves skin texture</li>
                    <li>• Boosts confidence</li>
                    <li>• No downtime required</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">45-60 minutes per session</span>
                  </div>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Treatment Process */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Your Treatment Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From consultation to results, we guide you through every step of your wellness transformation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground">Consultation</h3>
              <p className="text-sm text-muted-foreground">
                Comprehensive assessment of your needs and goals with our professional team
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground">Personalized Plan</h3>
              <p className="text-sm text-muted-foreground">
                Custom treatment plan designed specifically for your unique requirements
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground">Treatment</h3>
              <p className="text-sm text-muted-foreground">
                Professional treatment sessions using advanced equipment and techniques
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground">Results & Support</h3>
              <p className="text-sm text-muted-foreground">
                Ongoing monitoring and support to maintain and enhance your results
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Quality */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Safety & Quality Assurance</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Your safety and satisfaction are our top priorities in every treatment we provide
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Professional Standards</h3>
                <p className="text-muted-foreground leading-relaxed">
                  All treatments are performed by qualified professionals following strict safety protocols 
                  and hygiene standards.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Premium Equipment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We use only the latest, FDA-approved equipment and technology to ensure safe, 
                  effective treatments.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Personalized Care</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Every treatment is tailored to your individual needs with thorough consultations 
                  and ongoing support.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Transform Your Wellness?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Book your consultation today and take the first step towards achieving your beauty and wellness goals.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp Us Now
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;